import { Injectable } from '@angular/core';
import { Event, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { removeAccents } from '@helpers/functions';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

export interface Portal {
    titulo: string;
    link: string;
}

export type Portais = Portal[];

@Injectable({
    providedIn: 'root',
})
export class PortalService {
    // LISTA DE PORTAIS
    private readonly portaisSubject = new BehaviorSubject<Portais>([]);
    readonly portais$ = this.portaisSubject.asObservable();

    // Loading lista
    private readonly loadingSubject = new BehaviorSubject<boolean>(true);
    readonly loading$ = this.loadingSubject.asObservable();

    // PORTAL ATUAL
    private readonly portalAtualSubject = new BehaviorSubject<
        string | undefined
    >(undefined);
    readonly portalAtual$ = this.portalAtualSubject
        .asObservable()
        .pipe(map(portal => portal?.toLowerCase()));

    // Portal atual em lowercase
    get portal() {
        return this.portalAtualSubject.value?.toLowerCase();
    }

    // Título do portal atual
    private readonly tituloSubject = new BehaviorSubject<string>('');
    readonly titulo$ = this.tituloSubject.asObservable();

    constructor(private router: Router) {
        this.iniciaPortais();

        // Define imediatamente com a URL do browser considerando HashLocationStrategy (antes dos guards)
        if (typeof window !== 'undefined' && window.location) {
            const hash = (window.location.hash as string) || '';
            const path =
                hash.startsWith('#') && hash.length > 1
                    ? hash.substring(1)
                    : (window.location.pathname as string);
            if (path) this.setPortalAtual(path);
        }
        this.observarRota();
    }

    // Observa se a lista de portais mudou
    private iniciaPortais(): void {
        this.loadingSubject.next(true);
    }

    // Observa mudanças na rota para definir o portal atual
    private observarRota(): void {
        this.router.events.subscribe((e: Event) => {
            if (e instanceof NavigationStart) {
                // Define o portal o quanto antes (antes dos guards)
                this.setPortalAtual(e.url);
            } else if (e instanceof NavigationEnd) {
                // Garante a URL final após redirecionamentos
                this.setPortalAtual(e.urlAfterRedirects);
            }
        });
    }

    // Portais do usuário
    setPortais(portais: string[]): void {
        const portaisFormatados: Portais = portais
            .map(portal => ({
                titulo: portal,
                link: `/${removeAccents(portal)}`,
            }))
            .filter(portal => portal.link !== null);
        this.portaisSubject.next(portaisFormatados);
        this.loadingSubject.next(false);
    }

    getPortais(): Portais {
        return this.portaisSubject.value;
    }

    // Portal atual
    private setPortalAtual(routeUrl: string): void {
        let portalAtual: string | undefined;

        if (routeUrl.startsWith('/publico')) {
            portalAtual = 'Público';
        } else if (routeUrl.startsWith('/colaborador')) {
            portalAtual = 'Colaborador';
        } else if (routeUrl.startsWith('/administrador')) {
            portalAtual = 'Administrador';
        } else if (routeUrl.startsWith('/coordenador')) {
            portalAtual = 'Coordenador';
        } else if (routeUrl.startsWith('/gestor')) {
            portalAtual = 'Gestor';
        } else if (
            routeUrl.startsWith('/auth') ||
            routeUrl.startsWith('/pages')
        ) {
            portalAtual = undefined;
        } else {
            console.warn(`Rota não mapeada para portal: ${routeUrl}`);
            portalAtual = undefined;
            this.tituloSubject.next('');
        }

        // Só atualiza se o portal mudou
        if (this.portalAtualSubject.value !== portalAtual) {
            this.tituloSubject.next(`Portal ${portalAtual}`);
            this.portalAtualSubject.next(portalAtual);
        }
    }

    getPortalAtual(): string | undefined {
        // Portal atual formato original
        return this.portalAtualSubject.value;
    }
}
